import type { ReactNode } from 'react';
import { EmptyState, Spinner } from '@busbook/ui';

/**
 * A dispatcher's table: hairline rules, tabular figures, no zebra striping.
 * Dense enough to scan a day of departures without scrolling past the fold.
 */

export interface Column<T> {
  key: string;
  header: string;
  /** Right-align numeric columns so digits line up. */
  numeric?: boolean;
  width?: string;
  render: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  empty,
  onRowClick,
}: {
  columns: Column<T>[];
  rows: T[] | null;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: string | null;
  empty?: { title: string; description?: string; action?: ReactNode };
  onRowClick?: (row: T) => void;
}) {
  if (loading) {
    return (
      <div className="grid place-items-center py-16">
        <Spinner label="Loading" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-10 text-center text-sm" style={{ color: 'var(--signal-cancelled)' }}>
        {error}
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return <EmptyState title={empty?.title ?? 'Nothing here yet'} description={empty?.description} action={empty?.action} />;
  }

  return (
    <div
      className="overflow-x-auto rounded-xl border"
      style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
    >
      <table className="w-full border-collapse text-[0.8125rem]">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`border-b px-4 py-2.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] ${
                  column.numeric ? 'text-right' : 'text-left'
                }`}
                style={{ borderColor: 'var(--color-line)', color: 'var(--color-faint)', width: column.width }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? 'cursor-pointer transition-colors hover:bg-[var(--ink-800)]' : 'transition-colors hover:bg-[var(--ink-800)]'}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`border-b px-4 py-3 align-middle ${
                    column.numeric ? 'text-right tabular' : 'text-left'
                  }`}
                  style={{ borderColor: 'var(--color-line)' }}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Modal used for every create/edit form in the console. */
export function Drawer({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,.62)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex h-full w-full max-w-lg flex-col border-l shadow-xl"
        style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline-strong)' }}
      >
        <header
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: 'var(--color-line)' }}
        >
          <h2 className="font-display text-[1rem] font-bold tracking-tight">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-lg leading-none">
            ×
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <footer
            className="flex justify-end gap-2 border-t px-6 py-4"
            style={{ borderColor: 'var(--color-line)' }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/** Status pill using the operational signal palette. */
export function StatusBadge({ status }: { status: string }) {
  const color =
    status === 'CANCELLED'
      ? 'var(--signal-cancelled)'
      : status === 'DELAYED'
        ? 'var(--signal-delayed)'
        : status === 'DEPARTED'
          ? 'var(--signal-departed)'
          : status === 'PENDING_PAYMENT'
            ? 'var(--signal-pending)'
            : 'var(--signal-scheduled)';

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap"
      style={{ background: `color-mix(in srgb, ${color} 13%, transparent)`, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {status.replace(/_/g, ' ').toLowerCase()}
    </span>
  );
}
